// ./client/src/slateUtils.ts

// Import necessary types and functions from Slate libraries
import {
    Editor,
    Transforms,
    Element as SlateElement,
    Text,
    BaseEditor,
    Descendant,
    Node // Import Node for Node.string
} from 'slate';
import { ReactEditor } from 'slate-react';
import { HistoryEditor } from 'slate-history';

// --- Custom Types ---
// Define the structure for paragraph elements in the editor
export type ParagraphElement = {
    type: 'paragraph';
    align?: 'left' | 'center' | 'right'; // Optional alignment property
    children: CustomText[]; // Paragraphs contain text nodes
};

// Union type for all possible custom elements in the editor
export type CustomElement = ParagraphElement; // Add other element types here | HeadingElement | ...

// Define the structure for text nodes, including potential formatting marks
export type FormattedText = {
    text: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    color?: string; // Optional color mark
};

// Type for custom text nodes (usually FormattedText)
export type CustomText = FormattedText;

// Extend Slate's BaseEditor with React and History capabilities, using custom types
export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor & {
    // Add any custom editor methods if needed
};

// --- Type Guards ---
// Type guard to check if a node is a CustomElement
const isCustomElement = (node: unknown): node is CustomElement => {
    return SlateElement.isElement(node) && 'type' in node && typeof node.type === 'string';
};

// Type guard to check if a node is specifically a ParagraphElement
const isParagraphElement = (node: unknown): node is ParagraphElement => {
    return isCustomElement(node) && node.type === 'paragraph';
};


// --- Mark Helper Functions ---

/**
 * Checks if a specific formatting mark is active at the current selection.
 * @param editor The Slate editor instance.
 * @param format The mark format to check (e.g., 'bold', 'italic').
 * @returns True if the mark is active, false otherwise.
 */
export const isMarkActive = (editor: CustomEditor, format: keyof Omit<FormattedText, 'text'>): boolean => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};

/**
 * Toggles a specific formatting mark (bold, italic, underline) on or off for the current selection.
 * @param editor The Slate editor instance.
 * @param format The mark format to toggle.
 */
export const toggleMark = (editor: CustomEditor, format: keyof Omit<FormattedText, 'text'>) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
        Editor.removeMark(editor, format);
    } else {
        // Ensure 'color' isn't accidentally added here if toggling bold/italic/underline
        if (format !== 'color') {
             Editor.addMark(editor, format, true);
        }
    }
};

/**
 * Applies a specific color mark to the current selection. Removes the mark if 'white' is selected.
 * @param editor The Slate editor instance.
 * @param color The color string (e.g., '#FF0000', 'rgb(255,0,0)', or 'white').
 */
export const setColorMark = (editor: CustomEditor, color: string) => {
    if (color && color !== 'white' && color !== 'rgb(255, 255, 255)') {
        // Add or update the color mark for non-default colors
        Editor.addMark(editor, 'color', color);
    } else {
        // Remove the color mark if 'white' or an equivalent is selected
        Editor.removeMark(editor, 'color');
    }
};

/**
 * Gets the active color mark at the current selection.
 * @param editor The Slate editor instance.
 * @returns The active color string, or 'white' if no color mark is active.
 */
export const getCurrentColor = (editor: CustomEditor): string => {
    const marks = Editor.marks(editor);
    // Return the color mark if it exists, otherwise default to 'white'
    return marks?.color || 'white';
};


// --- Block (Alignment) Helper Functions ---

/**
 * Checks if a specific alignment is active for the paragraph node(s) at the current selection.
 * @param editor The Slate editor instance.
 * @param alignment The alignment to check ('left', 'center', 'right').
 * @returns True if the alignment is active, false otherwise.
 */
export const isAlignmentActive = (editor: CustomEditor, alignment: 'left' | 'center' | 'right'): boolean => {
    const [match] = Editor.nodes<CustomElement>(editor, {
        match: n => isParagraphElement(n) && n.align === alignment,
        mode: 'lowest', // Check the paragraph node directly containing the selection
    });
    return !!match; // Returns true if a matching node is found
};

/**
 * Gets the current alignment of the paragraph node at the selection.
 * @param editor The Slate editor instance.
 * @returns The current alignment ('left', 'center', 'right'). Defaults to 'left'.
 */
export const getCurrentAlignment = (editor: CustomEditor): 'left' | 'center' | 'right' => {
     const [match] = Editor.nodes<CustomElement>(editor, {
        match: n => isParagraphElement(n), // Match any paragraph node
         mode: 'lowest', // Get the lowest block node containing the selection
    });

    // If a matching paragraph is found, return its alignment or default to 'left'
    if (match && isParagraphElement(match[0])) {
        return match[0].align || 'left';
    }
    // Default alignment if no paragraph node is found at selection (should be rare)
    return 'left';
};


/**
 * Toggles the text alignment for the paragraph node(s) at the current selection.
 * If the target alignment is already active, it reverts to default (left).
 * @param editor The Slate editor instance.
 * @param alignment The alignment to apply ('left', 'center', 'right').
 */
export const toggleAlignment = (editor: CustomEditor, alignment: 'left' | 'center' | 'right') => {
    const currentAlignment = getCurrentAlignment(editor);
    const newAlignment = currentAlignment === alignment ? undefined : alignment; // Toggle off if active, else set

    // Apply the new alignment to all paragraph nodes within the selection's scope
    Transforms.setNodes<SlateElement>(
        editor,
        { align: newAlignment },
        // Ensure we only modify paragraph elements
        { match: n => SlateElement.isElement(n) && n.type === 'paragraph' }
    );
};


// --- Serialization / Deserialization ---
// IMPORTANT: Robust HTML handling is complex. These are simplified examples.
// Consider dedicated libraries (e.g., integrating with 'rehype') and ALWAYS sanitize on the backend.

/**
 * Deserializes an HTML string into an array of Slate Descendant nodes.
 * @param htmlString The HTML string to deserialize.
 * @returns An array of Slate Descendant nodes.
 */
export const deserializeHTML = (htmlString: string): Descendant[] => {
    // Handle empty or null input
    if (!htmlString?.trim()) {
        // Return a default valid Slate structure: a single empty paragraph
        return [{ type: 'paragraph', align: 'left', children: [{ text: '' }] }];
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Recursive function to convert DOM nodes to Slate nodes
    const deserializeNodes = (node: Node): Descendant | Descendant[] | null => {
        // --- Handle Text Nodes ---
        if (node.nodeType === Node.TEXT_NODE) {
            // Ignore pure whitespace nodes unless they contain a newline (from <br>)
            if (!node.textContent?.trim() && !node.textContent?.includes('\n')) return null;
             // Keep newline characters which might come from <br> tags processed earlier
            return { text: node.textContent || '' };
        }

        // --- Handle Element Nodes ---
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null; // Skip comments, processing instructions, etc.
        }

        const element = node as HTMLElement;
        let children = Array.from(element.childNodes).flatMap(deserializeNodes).filter(Boolean) as Descendant[];

        // If an element has no valid children, ensure it has at least an empty text node for Slate validity
        if (children.length === 0) {
            children = [{ text: '' }];
        }

        // --- Map HTML tags to Slate structure ---
        switch (element.nodeName) {
            // Block Elements
            case 'P':
            case 'DIV': // Treat DIVs like paragraphs
                const align = element.style.textAlign as 'left' | 'center' | 'right' | undefined;
                // Ensure children are valid text nodes for a paragraph
                const paragraphChildren = ensureTextChildren(children);
                return { type: 'paragraph', align: align || 'left', children: paragraphChildren };

            // Inline Elements (Marks)
            case 'STRONG':
            case 'B':
                return children.map(child => Text.isText(child) ? { ...child, bold: true } : child);
            case 'EM':
            case 'I':
                return children.map(child => Text.isText(child) ? { ...child, italic: true } : child);
            case 'U':
                return children.map(child => Text.isText(child) ? { ...child, underline: true } : child);
            case 'SPAN':
                // Apply color mark if span has a color style
                if (element.style.color) {
                    // Convert color to a consistent format if possible (e.g., rgb to hex) - optional
                    const color = element.style.color;
                    return children.map(child => Text.isText(child) ? { ...child, color: color } : child);
                }
                // If span has no recognized style, just return its children
                return children;

            // Other Elements
            case 'BR':
                // Represent <br> as a newline character in a text node
                return { text: '\n' };

            // Default: Handle unrecognized tags
            default:
                // If it seems like a block element (contains paragraphs), return children directly
                if (children.some(c => SlateElement.isElement(c))) {
                     return children;
                }
                 // Otherwise, wrap its text content in a paragraph
                 return { type: 'paragraph', align: 'left', children: ensureTextChildren(children) };
        }
    };

    // Process the main body of the parsed HTML
    let slateNodes = Array.from(doc.body.childNodes).flatMap(deserializeNodes).filter(Boolean) as Descendant[];

    // --- Final Cleanup and Validation ---
    // 1. Ensure top level contains only block elements (wrap stray text nodes)
    slateNodes = slateNodes.map(node => {
        if (Text.isText(node)) {
            // Wrap top-level text nodes (including those resulting from <br>) in paragraphs
            return { type: 'paragraph', align: 'left', children: [node] };
        }
        return node;
    });

    // 2. Ensure there's at least one block element (default to empty paragraph if needed)
    if (slateNodes.length === 0 || slateNodes.every(Text.isText)) {
        slateNodes = [{ type: 'paragraph', align: 'left', children: [{ text: '' }] }];
    }

    // 3. Ensure all elements are valid CustomElements (convert if necessary)
    const ensureValidElements = (nodes: Descendant[]): Descendant[] => {
        return nodes.map(node => {
            if (SlateElement.isElement(node)) {
                // If it's not a recognized CustomElement type, convert to paragraph
                 if (node.type !== 'paragraph') { // Add checks for other valid types here
                    return { type: 'paragraph', align: 'left', children: ensureTextChildren(node.children) };
                }
                // Ensure children of valid elements are also valid
                return { ...node, children: ensureTextChildren(node.children) };
            }
            return node; // Keep text nodes as they are
        }).filter(Boolean) as Descendant[]; // Filter out any nulls from potential corrections
    };

    slateNodes = ensureValidElements(slateNodes);

     // Final safety check: if somehow everything got filtered out, return default
     if (slateNodes.length === 0) {
         return [{ type: 'paragraph', align: 'left', children: [{ text: '' }] }];
     }

    return slateNodes;
};

// Helper to ensure children array consists only of CustomText nodes
const ensureTextChildren = (children: Descendant[]): CustomText[] => {
    const texts: CustomText[] = [];
    children.forEach(child => {
        if (Text.isText(child)) {
            texts.push(child);
        } else if (SlateElement.isElement(child)) {
            // If an element is nested where only text should be, extract its text content
            texts.push({ text: Node.string(child) });
        }
    });
    // Ensure there's always at least one text node, even if empty
    if (texts.length === 0) {
        texts.push({ text: '' });
    }
    return texts;
};


/**
 * Serializes an array of Slate Descendant nodes into an HTML string.
 * @param nodes The Slate nodes to serialize.
 * @returns An HTML string representation.
 */
export const serializeHTML = (nodes: Descendant[]): string => {
    // Recursive function to convert a single Slate node to HTML
    const serializeNode = (node: Descendant): string => {
        // --- Handle Text Nodes ---
        if (Text.isText(node)) {
            // IMPORTANT: Escape HTML special characters in text content to prevent XSS
            let string = node.text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                 .replace(/\n/g, '<br/>'); // Convert newlines back to <br> tags

            // Apply marks by wrapping the string in corresponding HTML tags/spans
            if (node.bold) string = `<strong>${string}</strong>`;
            if (node.italic) string = `<em>${string}</em>`;
            if (node.underline) string = `<u>${string}</u>`;
            // Only add color span if the color mark exists and is not the default 'white'
            if (node.color && node.color !== 'white' && node.color !== 'rgb(255, 255, 255)') {
                // Basic sanitization for color value before embedding in style
                const sanitizedColor = node.color.replace(/[^a-zA-Z0-9#(),\s.%-]/g, '');
                string = `<span style="color: ${sanitizedColor};">${string}</span>`;
            }
            return string;
        }

        // --- Handle Element Nodes ---
        // Serialize children first
        const childrenHtml = node.children.map(serializeNode).join('');

        // Map Slate element types to HTML tags
        if (isParagraphElement(node)) {
             const align = node.align && node.align !== 'left' ? node.align : null;
             const style = align ? ` style="text-align: ${align};"` : '';
             // Browsers often collapse empty blocks, a <br> can help preserve space.
             return `<p${style}>${childrenHtml || '<br/>'}</p>`;
        }

        // Fallback for unknown element types (should not happen with strict types)
        return `<div>${childrenHtml}</div>`; // Wrap in a div as a safe default
    };

    // Join the HTML strings generated for each top-level node
    return nodes.map(serializeNode).join('');
};


// --- Editor Content Check ---

/**
 * Checks if the Slate editor value represents effectively empty content.
 * @param value The Slate editor value (array of Descendant nodes).
 * @returns True if the editor is considered empty, false otherwise.
 */
export const isEditorEmpty = (value: Descendant[]): boolean => {
    if (!value || value.length === 0) return true;
    // Check if it's a single paragraph node...
    return (
        value.length === 1 &&
        isParagraphElement(value[0]) &&
        // ...with no alignment set...
        !value[0].align &&
        // ...containing only a single child node...
        value[0].children.length === 1 &&
        // ...which is a text node with empty content.
        Text.isText(value[0].children[0]) &&
        value[0].children[0].text === '' &&
        // ...and has no marks applied to the empty text node.
         Object.keys(value[0].children[0]).length === 1 // Only the 'text' property should exist
    );
};


// --- Word Count ---

/**
 * Calculates the approximate word count of the content in the Slate editor.
 * @param value The Slate editor value (array of Descendant nodes).
 * @returns The number of words.
 */
export const getWordCount = (value: Descendant[]): number => {
     // Get the plain text content of all nodes
     const text = value.map(n => Node.string(n)).join('\n');
     // Split by whitespace and filter out empty strings
     return text.trim().split(/\s+/).filter(Boolean).length;
};

